import type { Quote, StorefrontConfig, Tier } from '../lib/api';
import type { QuoteState } from '../hooks/useQuote';
import { formatHashrate, formatSavingsBasisPoints } from '../lib/format';

/**
 * Choose your mining power.
 *
 * THE SLIDER IS THE PRIMARY INTERACTION. The six tiers sit beneath it as compact
 * presets: quick ways to move the slider and landmarks for the volume discount, rather
 * than six product cards competing for the decision. That is the configurator shape
 * Sidney asked for.
 *
 * ANY WHOLE AMOUNT IN RANGE IS NOW BUYABLE. Bracketed per-TH pricing has been confirmed
 * and implemented server-side, so a position between presets is an ordinary purchase
 * rather than a preview. The presets are shortcuts to common amounts, nothing more.
 *
 * WHAT HAS NOT CHANGED, AND MUST NOT: this component computes no price and no saving. It
 * displays what the server quoted for the exact amount on screen, and its bounds come
 * from the server's own priceable range rather than from constants here -- a slider that
 * could reach a value the server would refuse is precisely the bug that avoids.
 */

interface HashratePickerProps {
  config: StorefrontConfig;
  valueTh: number;
  termDays: number;
  selectedTier: Tier | null;
  /** The server's answer for the amount currently on screen. */
  quoteState: QuoteState;
  onChange: (hashrateTh: number) => void;
}

/**
 * The preset caption.
 *
 * Reads the server's published figure for that tier and nothing else -- no percentage is
 * computed, rounded up from, or inferred here. The baseline tier says so plainly rather
 * than showing "Save 0%".
 *
 * The HEADLINE saving is a different figure with a different source: it comes from the
 * quote for the exact amount on screen, so a custom amount gets its band's saving from
 * the server rather than from anything this file could work out.
 */
function savingsLabel(tier: Tier, termDays: number): string {
  // The COMBINED figure -- hashrate and duration together -- so a preset card and the
  // duration option for the same configuration can never show two different numbers.
  return formatSavingsBasisPoints(tier.combinedSavingsBasisPointsByTermDays?.[String(termDays)]);
}

/**
 * The band, written out.
 *
 * READ FROM THE SERVER'S QUOTE, NEVER WORKED OUT HERE. The boundaries arrive on
 * `quote.bracket`; this function only punctuates them. Deriving "which band is 608 in?"
 * in the browser would be a second copy of the bracket rule, and the day it disagreed
 * with the server a customer would be told one band and charged another.
 *
 * One unit suffix rather than two, because "500 TH/s-999 TH/s" reads worse than the
 * range it describes. The open-topped band is written with a "+".
 */
function bandLabel(bracket: Quote['bracket']): string {
  return bracket.maxTh === null
    ? `${bracket.minTh}+ TH/s`
    : `${bracket.minTh}–${bracket.maxTh} TH/s`;
}

/**
 * What to say beneath the slider.
 *
 * Never a price -- the summary owns that -- and never a promise about allocation, which
 * PowerPool has not confirmed.
 *
 * For a preset it names the product. For a custom amount it explains WHY the price is
 * what it is, which is the one thing the screen does not already say: the hashrate is
 * displayed above in large type, so repeating it here bought nothing, while the band is
 * the reason 608 TH/s costs what it does.
 */
function availabilityLine({
  selectedTier,
  quoteState,
}: {
  selectedTier: Tier | null;
  quoteState: QuoteState;
}): string {
  if (quoteState.status === 'unpriceable') return quoteState.reason;
  if (quoteState.status === 'error') return 'Could not load a price just now.';
  if (selectedTier) return `${selectedTier.label} · ${selectedTier.displayHashrate}`;
  if (quoteState.status === 'ready') return `${bandLabel(quoteState.quote.bracket)} rate applied`;
  return 'Pricing…';
}

export default function HashratePicker({
  config,
  valueTh,
  termDays,
  selectedTier,
  quoteState,
  onChange,
}: HashratePickerProps) {
  const tiers = config.tiers;

  // The bounds and the step are the server's priceable range, not constants. If Sidney
  // widens the price list, the slider widens with it and nothing here changes.
  const min = config.customHashrate.minTh;
  const max = config.customHashrate.maxTh;
  const step = config.customHashrate.incrementTh;

  return (
    <div className="mm-panel p-5 sm:p-6">
      {/*
        The configured amount, and nothing else.

        THE SAVINGS LINE AND THE DURATION LINE WERE REMOVED FROM HERE at Sidney's
        request. Both figures still exist and are still the server's: the per-hashrate
        saving is on the preset cards below, and the per-duration saving is on the
        duration options in Step 2. This heading is now one number.

        IT WAS ALSO THE FLICKER. The savings line rendered only when a READY quote said
        the saving was positive, so every preset click unmounted it -- the quote went to
        `loading`, the paragraph vanished, the card shrank by its height, and it came
        back when the quote landed. That is the layout jump, once per click. Removing it
        removes the cause rather than papering over it, and there is nothing conditional
        left in this heading to reintroduce it.
      */}
      <div className="text-center">
        <p className="mm-numeric text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="picker-hashrate">
          {formatHashrate(valueTh)}
        </p>
      </div>

      <input
        id="hashrate-slider"
        type="range"
        min={min}
        max={max}
        step={step}
        value={valueTh}
        onChange={(event) => onChange(Number(event.target.value))}
        aria-label="Mining power"
        aria-describedby="hashrate-availability"
        aria-valuetext={formatHashrate(valueTh)}
        className="mm-range mt-5"
      />

      <div className="mm-numeric mm-help flex justify-between text-xs">
        <span>{formatHashrate(min)}</span>
        <span>{formatHashrate(max)}</span>
      </div>

      {/* Presets: quick selections, not products. */}
      <fieldset className="m-0 mt-5 border-0 p-0">
        <legend className="sr-only">Quick select a hashrate</legend>

        <div
          className="grid grid-cols-3 gap-2 sm:grid-cols-6"
          role="radiogroup"
          aria-label="Hashrate tier"
        >
          {tiers.map((tier) => {
            const selected = tier.id === selectedTier?.id;

            return (
              <button
                key={tier.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => onChange(tier.hashrateTh)}
                data-testid={`tier-${tier.id}`}
                data-selected={selected}
                className="mm-preset"
              >
                <span className="mm-numeric text-sm font-semibold">{tier.displayHashrate}</span>
                <span className="mm-preset-saving">{savingsLabel(tier, termDays)}</span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {/*
        One line, not a paragraph.

        It no longer gates anything -- every amount in range is purchasable -- so it says
        what has been selected. A named preset is named; anything else is stated as the
        custom amount it is, which is a fact about the order, not a warning about it.
      */}
      <p
        id="hashrate-availability"
        role="status"
        data-testid="hashrate-availability"
        className="mm-help mt-4 text-center text-xs"
      >
        {availabilityLine({ selectedTier, quoteState })}
      </p>
    </div>
  );
}
